<script setup lang="ts">
import { computed } from 'vue';
import type { ConnectionTool } from '../model.js';
import type { DirectoryDevice } from '../lib/directory.js';
import { formatRelativeTime, formatTimestamp, presenceLabel, presenceTone } from '../lib/format.js';
import AppIcon from './AppIcon.vue';
import DeviceGlyph from './DeviceGlyph.vue';
import OsIdentity from './OsIdentity.vue';
import MenuButton from './MenuButton.vue';
import MenuItem from './MenuItem.vue';
import StatusPill from './StatusPill.vue';

const props = defineProps<{
  device: DirectoryDevice;
  opening: boolean;
  preferencesBusy?: boolean;
  removable?: boolean;
}>();
const emit = defineEmits<{
  select: [];
  open: [tool: ConnectionTool];
  customize: [];
  remove: [];
}>();

const seen = computed(() =>
  props.device.source === 'saved'
    ? ''
    : props.device.presence === 'online'
      ? 'Online now'
      : props.device.lastSeenAt
        ? `Seen ${formatRelativeTime(props.device.lastSeenAt)}`
        : '',
);
const renamed = computed(() => props.device.name !== props.device.reportedName);
</script>

<template>
  <article class="device-row" :data-presence="props.device.presence">
    <DeviceGlyph
      :icon="props.device.icon"
      :color="props.device.color"
      :platform="props.device.reportedPlatform ?? props.device.platform"
      :os-version="props.device.osVersion"
      :presence="props.device.presence"
      size="sm"
    />
    <button
      type="button"
      class="device-row-main"
      :title="renamed ? `Reported as ${props.device.reportedName}` : undefined"
      @click="emit('select')"
    >
      <span class="device-name">{{ props.device.name }}</span>
      <span class="device-host">{{ props.device.hostname }}</span>
      <span class="sr-only">Connection details for {{ props.device.name }}</span>
    </button>
    <OsIdentity
      class="device-row-system"
      :platform="props.device.reportedPlatform ?? props.device.platform"
      :version="props.device.osVersion"
      :fallback="props.device.source === 'saved' ? 'SSH alias' : 'System not reported'"
    />
    <StatusPill :tone="presenceTone[props.device.presence]">
      {{ presenceLabel[props.device.presence] }}
    </StatusPill>
    <span class="device-row-seen" :title="formatTimestamp(props.device.lastSeenAt)">{{
      seen
    }}</span>
    <div class="device-row-actions">
      <span v-if="device.hostProfile === 'linux-server'" class="tag">Headless server</span>
      <button
        v-if="device.hostProfile !== 'linux-server'"
        type="button"
        class="small"
        :disabled="!props.device.desktop.available || props.opening"
        :title="props.device.desktop.explanation"
        @click="emit('open', 'desktop')"
      >
        <AppIcon name="devices" />Desktop<span class="sr-only"> on {{ props.device.name }}</span>
      </button>
      <button
        v-if="!props.device.personalAccess"
        type="button"
        class="small"
        :disabled="!props.device.terminal.available || props.opening"
        :title="props.device.terminal.explanation"
        @click="emit('open', 'terminal')"
      >
        <AppIcon name="terminal" />Terminal<span class="sr-only"> on {{ props.device.name }}</span>
      </button>
    </div>
    <MenuButton :label="`More actions for ${props.device.name}`" small>
      <MenuItem icon="edit" :disabled="props.preferencesBusy" @click="emit('customize')"
        >Customize…</MenuItem
      >
      <MenuItem
        v-if="device.hostProfile !== 'linux-server'"
        icon="devices"
        :disabled="!props.device.desktop.available || props.opening"
        :hint="props.device.desktop.explanation"
        @click="emit('open', 'desktop')"
      >
        Open desktop
      </MenuItem>
      <MenuItem
        v-if="!props.device.personalAccess"
        icon="terminal"
        :disabled="!props.device.terminal.available || props.opening"
        :hint="props.device.terminal.explanation"
        @click="emit('open', 'terminal')"
      >
        Open terminal
      </MenuItem>
      <MenuItem icon="chevronRight" @click="emit('select')">Device page</MenuItem>
      <MenuItem v-if="props.removable" icon="trash" @click="emit('remove')">Remove…</MenuItem>
    </MenuButton>
  </article>
</template>
